import { Component, Inject, Input, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG } from '@app/config/app.config';
import { IAppConfig } from '@app/config/iapp.config';
import { ProgressBarService } from '@app/shared/services/progress-bar.service';
import { InfoService } from '@app/core/info/info.service';
import { AuthenticationService } from '@app/core/auth/authentication.service';
import { MatDrawerContainer } from '@angular/material/sidenav';
import { Store } from '@ngrx/store';
import { selectorAuth, ActionAuthLogout, SessionStorageService } from '@app/core';
import { IUserData } from '@app/profile/main/details';
import { iconCodes } from '@app/shared/cqes-icon/cqes-icon.component';
import { Router } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { BusinessService } from '@app/business/business.service';
import { BissService } from '@app/shared/services/biss.service';

@Component({
  selector: 'cloud-app-nav',
  templateUrl: './nav.component.html',
  styleUrls: [
    './nav.component.scss',
    './nav.component-theme.scss'
  ]
})

export class NavComponent implements OnInit, OnDestroy {
  @Input() navElements: [{}];
  @Input() sidenav: MatDrawerContainer;
  @Input() logo: string;

  appConfig: any;
  menuItems: any[];
  progressBarMode: string;
  isAuthenticated: boolean;
  userData: IUserData;
  icons = iconCodes;
  currentNavElements: any[];

  isEmergencyLogin: boolean;
  currentView = '';
  businessAccountId;
  hasBusinessAccount = false;
  businessLogo: string;
  subscription;
  isLoggedInOnlyQES: boolean;
  private unsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    @Inject(APP_CONFIG) appConfig: IAppConfig,
    public info: InfoService,
    private progressBarService: ProgressBarService,
    private store: Store<any>,
    public translateService: TranslateService,
    public auth: AuthenticationService,
    private cdRef: ChangeDetectorRef,
    private sessionStorageService: SessionStorageService,
    private businessService: BusinessService,
    private router: Router,
    public bissService: BissService,
  ) {
    this.appConfig = appConfig;
  }

  getLogoAndSetCurrentCompany() {
    if (this.checkLogo()) {
      this.businessService.getCompany(this.businessAccountId).subscribe(res => {
        this.sessionStorageService.setItem("currentCompany", res.data);
        this.businessLogo = 'data:image/' + this.getType(res.data.logo) + ';base64,' + res.data.logo;
      })
    }
  }


  ngOnInit(): void {
    this.getLogoAndSetCurrentCompany();

    this.subscription = this.sessionStorageService.businessAccountId.subscribe(id => {
      if (id) {
        this.businessAccountId = id;
        this.hasBusinessAccount = true;
        this.getLogoAndSetCurrentCompany();
      }
    });

    this.currentView = 'signing';
    if (window.location.pathname.includes('profile')) {
      this.currentView = 'profile';
    }

    this.setCurrentView(this.currentView);
    this.progressBarService.updateProgressBar$
      .subscribe((mode: string) => {
        this.progressBarMode = mode;
        this.cdRef.detectChanges();
      });

    this.store
      .select(selectorAuth)
      .subscribe(auth => {
        if (auth != null) {
          this.isAuthenticated = auth.isAuthenticated;
          this.userData = auth.userData;
          if (this.userData == undefined) {
            this.userData = this.sessionStorageService.getItem("loginEventData")
          }
          if (auth.alternativaLoginRemindedSuccess) {
            this.currentView = 'profile';
            this.setCurrentView(this.currentView);
          } else {
            this.currentView = 'signing';
            this.setCurrentView(this.currentView);
          }
        }
      });
    this.auth.isLoggedInOnlyQES$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(status => {
        this.isLoggedInOnlyQES = status;
      });
  }

  get lang() {
    return this.translateService.currentLang;
  }

  logout(): void {
    this.resetBusinessData()
    // this.bissService.logoutQes()
    this.store.dispatch(new ActionAuthLogout(false));
    this.sessionStorageService.clear();
  }

  resetBusinessData() {
    this.sessionStorageService.setItem("accounts", null);
    this.sessionStorageService.setItem("businessAccountId", null);
    this.sessionStorageService.setItem("settings", null);
    this.sessionStorageService.setItem("loginEventData", null);
    this.auth.loginOnlyQESShowProfile(false);
    this.hasBusinessAccount = false;
    this.businessAccountId = null;
    this.sessionStorageService.businessAccountId.next(null);
  }

  setCurrentView(view) {
    if (this.businessAccountId && this.businessAccountId != "personal") {
      this.router.navigateByUrl('/business/profile');
    }
    this.currentNavElements = [...this.navElements];
    this.currentView = view;
    if (this.currentView === 'signing') {
      const index = this.currentNavElements.findIndex(e => e['link'] === 'signing');
      this.currentNavElements.splice(index, 1);
    }
  }

  getName() {
    return this.userData?.firstName[this.lang] + " " + this.userData?.lastName[this.lang] + this.getCompanyName();
  }

  getCompanyName() {
    if (!this.businessAccountId || this.businessAccountId == "personal") {
      return '';
    } else {
      const company = this.sessionStorageService.getItem("accounts").find(item => item.cmrId === this.businessAccountId);
      return this.lang == 'bg' ? ` [ ${company?.nameBg ?? ""} ] ` : ` [ ${company.nameEn ?? ""} ] `;
    }
  }

  goToChangeAccount() {
    this.router.navigateByUrl('/accounts');
  }

  showProfileIcon() {
    return this.isAuthenticated && (!this.businessAccountId || this.businessAccountId == 'personal');
  }

  checkLogo() {
    return this.isAuthenticated && this.businessAccountId && this.businessAccountId != 'personal';
  }

  getType(imageBase64) {
    let extension = undefined;
    const decodedString = window.atob(imageBase64); //decode the string;
    const lowerCase = decodedString.toLowerCase();
    // find the extension in the decoded string
    if (lowerCase.indexOf("png") !== -1) extension = "png"
    else if (lowerCase.indexOf("svg") !== -1) extension = "svg+xml"
    else if (lowerCase.indexOf("jpg") !== -1 || lowerCase.indexOf("jpeg") !== -1)
      extension = "jpg"
    // add more cases if needed..
    return extension;
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
